// external
import React, { FC, useEffect, useState } from 'react';
import {
  TextField,
  Typography,
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import { palette } from '../../../theme';
import { FieldErrors, useFieldErrors, scrollToFirstError } from '../../form-validation';

// internal
import COETrainingDraftModal from '../coe-training-draft-modal/coe-training-draft-modal.component';
import '../coe-training-draft-modal/coe-training-draft-modal.component.css';
import TrainingDraftStepper from '../training-draft-stepper/training-draft-stepper.component';
import '../training-draft-stepper/training-draft-form.css';
import COEActionBarComponent from '../../coe-action-bar/coe-action-bar.component';
import useService from '../../../hooks/service.hook';
import TeamsService from '../../../services/teams.service';
import GroupsService from '../../../services/groups.service';
import COEMemberPanel from '../../coe-member-panel/coe-member-panel.component';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import TrainingsService from '../../../services/trainings.service';
import TrainingLibraryService from '../../../services/training-library.service';
import CustomTrainingFormsService from '../../../services/custom-training-forms.service';
import { Group, Team, CustomTrainingForm } from '../../../models/training.models';
import { FIELD_TYPE_CONFIG } from '../../dialogs/custom-training-field.dialog';
import { FLOAT_VALIDATION_REGEX, INTEGER_VALIDATION_REGEX } from '../../../constants/regex.constants';
import COETrainingDurationInput from '../../coe-training-duration-input/coe-training-duration-input.component';
import {
  getFocusOptionsFromMapping,
  getSportMappings,
  getSportsOptionsFromMapping,
  getSportsTypeOptionsFromMapping,
} from '../../../helpers/app-context.helpers';
import { sortByLastName, getMeasurementUnit } from '../../../helpers/general.helpers';

interface CreateTrainingDraftProps {
  open: boolean;
  handleClose: any;
  handleRefresh: any;
  team: Team;
  week: any;
  type: 'new' | 'duplicate' | 'template-duplicate';
  trainingId?: string;
  defaultDate?: any;
  group?: Group;
}

const CreateTrainingDraftComponent: FC<CreateTrainingDraftProps> = (props) => {
  const EMPTY_ASSIGN_TRAINING_FORM = {
    team: props.team,
    assignees: [],
    title: '',
    time: props.defaultDate,
    location: '',
    description: '',
    sport: '',
    sport_type: '',
    focus: '',
    distance: 0,
    duration: 0,
    actualTimeEnabled: true,
    attendanceEnabled: true,
    group: null,
    postTrainingFormId: null as number | null,
    excludedGroupIds: [] as (string | number)[],
  };

  type CreateDraftField = 'title' | 'description' | 'time' | 'sport' | 'sport_type' | 'focus' | 'distance';

  // hooks
  const teamsService = useService(TeamsService);
  const trainingsService = useService(TrainingsService);
  const groupsService = useService(GroupsService);
  const trainingLibraryService = useService(TrainingLibraryService);
  const customFormsService = useService(CustomTrainingFormsService);
  const { showSnackbar } = useSnackbarHelper();

  // states
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeOptionsLoading, setAssigneeOptionsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const { fieldErrors, setFieldErrors, clearFieldError, resetFieldErrors } = useFieldErrors<CreateDraftField>();
  const [loading, setLoading] = useState<boolean>(false);
  const [trainingForm, setTrainingForm] = useState(EMPTY_ASSIGN_TRAINING_FORM);
  const [activeTab, setActiveTab] = useState(0);
  const [visitedTabs, setVisitedTabs] = useState<Set<number>>(new Set());
  const [selectedGroup, setSelectedGroup] = useState<string | number | null>(props.group?.id ?? 'team');
  const [groupOptions, setGroupOptions] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState<boolean>(false);
  const sportsMappings = getSportMappings();

  // Form attachment states
  const [availableForms, setAvailableForms] = useState<CustomTrainingForm[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState<CustomTrainingForm | null>(null);
  const [formDetailsLoading, setFormDetailsLoading] = useState(false);

  // Helper function to convert Date to "HH:mm" format
  const dateToTimeString = (date: Date | null): string => {
    if (!date) return '';
    return dayjs(date).format('HH:mm');
  };

  // Helper function to convert "HH:mm" string to Date object
  const timeStringToDate = (timeString: string, baseDate?: Date): Date | null => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    const base = baseDate ? dayjs(baseDate) : dayjs();
    return base.hour(hours).minute(minutes).second(0).millisecond(0).toDate();
  };

  // Helper function to combine date and time
  const combineDateAndTime = (date: any, time: any) => {
    if (!date && !time) return null;
    if (!date && time) {
      // If only time is provided, use today's date
      return dayjs().hour(dayjs(time).hour()).minute(dayjs(time).minute()).second(0).millisecond(0).toDate();
    }
    if (date && !time) {
      // If only date is provided, keep the existing time or use current time
      const existingTime = trainingForm.time ? dayjs(trainingForm.time) : dayjs();
      return dayjs(date).hour(existingTime.hour()).minute(existingTime.minute()).second(0).millisecond(0).toDate();
    }
    // Both date and time provided
    return dayjs(date).hour(dayjs(time).hour()).minute(dayjs(time).minute()).second(0).millisecond(0).toDate();
  };

  // Fetch available forms on mount
  useEffect(() => {
    fetchAvailableForms();
  }, []);

  // Add/update Team group whenever assigneeOptions change
  useEffect(() => {
    if (assigneeOptions.length > 0) {
      setGroupOptions((prevGroups) => {
        const teamGroup: Group = {
          id: 'team',
          name: 'Team',
          members: [...assigneeOptions],
        };

        // Get existing groups (excluding Team if it exists)
        const existingGroups = prevGroups.filter((g) => g.id !== 'team');

        // Add Team group at the beginning
        return [teamGroup, ...existingGroups];
      });
    }
  }, [assigneeOptions]);

  // Set initial assignees based on selectedGroup when assigneeOptions and groupOptions are ready
  // This handles both new training creation and duplication scenarios
  useEffect(() => {
    // Only set assignees if assigneeOptions are ready and assignees haven't been set yet
    if (assigneeOptions.length > 0 && trainingForm.assignees.length === 0) {
      let initialAssignees: any[] = [];

      if (selectedGroup === 'team') {
        // Use all team members (Team group is added by the first useEffect)
        initialAssignees = [...assigneeOptions];
      } else if (selectedGroup !== null) {
        // Find the selected group and use its members
        // Wait for groupOptions to include the Team group (added by first useEffect)
        // and potentially the specific group if it's not 'team'
        const selectedGroupData = groupOptions.find((g) => g.id === selectedGroup);
        if (selectedGroupData) {
          initialAssignees = [...(selectedGroupData.members || [])];
        } else {
          // Group not found yet - wait for it to load
          // This can happen when groups are still loading from the API
          return;
        }
      } else {
        // No group selected - no assignees should be selected
        initialAssignees = [];
      }

      setTrainingForm((prev) => ({
        ...prev,
        assignees: sortByLastName(initialAssignees),
      }));
    }
  }, [assigneeOptions, groupOptions, selectedGroup, trainingForm.assignees.length]);

  // gets the athletes associated with a team when a team is selected and loads groups
  useEffect(() => {
    // Only run when modal is open
    if (!props.open) {
      return;
    }

    // Reset assignees so the consolidated useEffect can set them based on selectedGroup
    setTrainingForm((prev) => ({
      ...prev,
      assignees: [],
    }));

    // Set selectedGroup early based on props.group (prioritize over draft.group_id when duplicating)
    const initialGroupId = props.group?.id ?? 'team';
    setSelectedGroup(initialGroupId);
    setTrainingForm((prev) => ({
      ...prev,
      group: initialGroupId === 'team' ? null : initialGroupId,
    }));

    // Load groups
    if (props.team?.groups && props.team.groups.length > 0) {
      const groupsWithoutTeam = props.team.groups.filter((g) => g.id !== 'team');
      setGroupOptions(groupsWithoutTeam);
    } else if (props.team?.id) {
      getGroups();
    }

    // Load athletes/training data
    if (props.type === 'template-duplicate') {
      // Load athletes first so assignee options are available
      getAthletes();
      setLoading(true);
      // Load template from training library (trainingId is the template id)
      trainingLibraryService
        .getTrainingTemplate(props.team.id as string, props.trainingId)
        .then((response) => {
          const template = response.data.data;
          // Templates don't have group_id, so use props.group or default to 'team'
          const groupId = props.group?.id ?? 'team';

          // Update selectedGroup
          setSelectedGroup(groupId);

          // Combine template time with provided date
          let finalTime = template.time;
          if (props.defaultDate) {
            // Extract time of day (hours/minutes) from template.time and apply to defaultDate
            const templateTime = dayjs(template.time);
            finalTime = dayjs(props.defaultDate)
              .hour(templateTime.hour())
              .minute(templateTime.minute())
              .second(0)
              .millisecond(0)
              .toDate();
          } else if (template.time) {
            finalTime = new Date(template.time);
          }

          setTrainingForm((prev) => ({
            ...prev,
            title: template.title,
            time: finalTime,
            location: template.location || '',
            description: template.description,
            sport: template.sport,
            sport_type: template.sport_type, // Template uses snake_case
            focus: template.focus,
            distance: template.distance || 0,
            duration: template.duration || 0,
            actualTimeEnabled: true, // Default value for templates
            group: groupId === 'team' ? null : groupId,
            assignees: [], // Reset assignees so consolidated useEffect can set them based on selectedGroup
            excludedGroupIds: [],
          }));
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          showSnackbar('An error occurred while loading the training template', 'error');
          props.handleClose();
        });
    } else if (props.trainingId && props.type === 'duplicate') {
      // Load athletes first so assignee options are available
      getAthletes();
      setLoading(true);
      trainingsService
        .getTrainingByTeamId(props.team.id as string, props.trainingId)
        .then((response) => {
          const draft = response.data.data;
          // If props.group is not provided, use draft.group_id, otherwise use props.group.id
          const groupId = props.group?.id ?? draft.group_id ?? 'team';

          // Update selectedGroup if it changed (e.g., draft had a different group)
          // Reset assignees so the consolidated useEffect can set them based on the new selectedGroup
          setSelectedGroup(groupId);

          setTrainingForm((prev) => ({
            ...prev,
            title: draft.title,
            time: draft.time,
            location: draft.location,
            description: draft.description,
            sport: draft.sport,
            sport_type: draft.sportType,
            focus: draft.focus,
            distance: draft.distance,
            duration: draft.duration,
            actualTimeEnabled: draft.actualTimeEnabled,
            attendanceEnabled: draft.attendanceEnabled ?? true,
            group: groupId === 'team' ? null : groupId,
            assignees: [], // Reset assignees so consolidated useEffect can set them based on selectedGroup
            postTrainingFormId: draft.postTrainingFormId || null,
            excludedGroupIds: draft.excludedGroupIds || [],
          }));
          // If form is attached, fetch its details
          if (draft.postTrainingFormId) {
            fetchFormDetails(draft.postTrainingFormId);
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          showSnackbar('An error occured while duplicating', 'error');
          props.handleClose();
        });
    } else {
      // Always load all team members
      getAthletes();
    }
  }, [props.trainingId, props.open, props.team?.id, props.group?.id, props.type]);

  /**
   * Fetches available forms for the team
   */
  const fetchAvailableForms = () => {
    setFormsLoading(true);
    customFormsService
      .listForms(props.team.id as string)
      .then((response) => {
        setAvailableForms(response.data.data || []);
        setFormsLoading(false);
      })
      .catch(() => {
        showSnackbar('Error loading custom training forms', 'error');
        setFormsLoading(false);
      });
  };

  /**
   * Fetches full details of a selected form
   */
  const fetchFormDetails = (formId: number) => {
    setFormDetailsLoading(true);
    customFormsService
      .getForm(props.team.id as string, formId)
      .then((response) => {
        setSelectedForm(response.data.data);
        setFormDetailsLoading(false);
      })
      .catch(() => {
        showSnackbar('Error loading form details', 'error');
        setFormDetailsLoading(false);
        setSelectedForm(null);
      });
  };

  /**
   * Handles form selection change
   */
  const handleFormSelect = (formId: number | null) => {
    setTrainingForm((values) => ({ ...values, postTrainingFormId: formId }));
    if (formId) {
      fetchFormDetails(formId);
    } else {
      setSelectedForm(null);
    }
  };

  /**
   * Grabs all members of the selected team. Returns to the home page on error.
   *
   */
  const getAthletes = () => {
    setAssigneeOptionsLoading(true);
    const teamId = (trainingForm.team as any)?.id ?? props.team?.id;
    if (teamId) {
      teamsService
        .getAllTeamMembers(teamId as string)
        .then((response) => {
          const members = Array.isArray(response.data.data) ? response.data.data : [];
          const sortedMembers = sortByLastName(members);
          setAssigneeOptions(sortedMembers);
          // Assignees will be set by the consolidated useEffect based on selectedGroup
          setAssigneeOptionsLoading(false);
        })
        .catch(() => {
          setAssigneeOptionsLoading(false);
          showSnackbar("An error occured while grabbing your teams' athletes. Please try again later", 'error');
          props.handleClose();
        });
    } else {
      setAssigneeOptionsLoading(false);
    }
  };

  /**
   * Fetches groups for the team if not already available
   */
  const getGroups = () => {
    setGroupsLoading(true);
    if (props.team?.id) {
      groupsService
        .getGroups(props.team.id)
        .then((response) => {
          const groupsWithoutTeam = (response.data.data || []).filter((g) => g.id !== 'team');
          setGroupOptions(groupsWithoutTeam);
          setGroupsLoading(false);
        })
        .catch(() => {
          setGroupsLoading(false);
          // Don't show error snackbar for groups, just set empty array
          setGroupOptions([]);
        });
    }
  };

  /**
   * Handles member selection changes
   */
  const handleMembersChange = (members: any[]) => {
    setTrainingForm((values) => ({ ...values, assignees: members }));
  };

  /**
   * Handles group selection changes and auto-selects/deselects group members
   */
  const handleGroupsChange = (groupId: string | number | null) => {
    let updatedAssignees: any[] = [];

    // Get the previously selected group (before it changes)
    const previouslySelectedGroup = groupOptions.find((g) => g.id === selectedGroup);
    const previouslySelectedGroupMemberIds = new Set((previouslySelectedGroup?.members || []).map((m: any) => m.id));

    if (groupId !== null) {
      // Group is selected: remove members from previous group, add new group members, keep manually selected
      const group = groupOptions.find((g) => g.id === groupId);
      const groupMembers = group?.members || [];
      const groupMemberIds = new Set(groupMembers.map((m: any) => m.id));

      // Start with new group members
      updatedAssignees = [...groupMembers];

      // Add any manually selected members that aren't in the new group or previous group
      trainingForm.assignees.forEach((member: any) => {
        if (!groupMemberIds.has(member.id) && !previouslySelectedGroupMemberIds.has(member.id)) {
          updatedAssignees.push(member);
        }
      });
    } else {
      // Group is deselected: remove members from the previously selected group, keep manually selected members
      updatedAssignees = trainingForm.assignees.filter(
        (member: any) => !previouslySelectedGroupMemberIds.has(member.id)
      );
    }

    setSelectedGroup(groupId);
    setTrainingForm((values) => ({
      ...values,
      assignees: sortByLastName(updatedAssignees),
      group: groupId,
    }));
  };

  /**
   * Handles excluded groups change and syncs assignees: remove members of newly excluded groups,
   * add members of newly un-excluded groups (mirrors handleGroupsChange behavior).
   */
  const handleExcludedGroupsChange = (ids: (string | number)[]) => {
    const prevExcluded = trainingForm.excludedGroupIds ?? [];
    const addedIds = ids.filter((id) => !prevExcluded.includes(id));
    const removedIds = prevExcluded.filter((id) => !ids.includes(id));

    let updatedAssignees = [...trainingForm.assignees];

    for (const groupId of addedIds) {
      const group = groupOptions.find((g) => g.id === groupId);
      if (!group?.members?.length) continue;
      const memberIdsToRemove = new Set(group.members.map((m: any) => m.id));
      updatedAssignees = updatedAssignees.filter((m: any) => !memberIdsToRemove.has(m.id));
    }

    const existingAssigneeIds = new Set(updatedAssignees.map((m: any) => m.id));
    for (const groupId of removedIds) {
      const group = groupOptions.find((g) => g.id === groupId);
      if (!group?.members?.length) continue;
      for (const member of group.members) {
        if (!existingAssigneeIds.has(member.id)) {
          updatedAssignees.push(member);
          existingAssigneeIds.add(member.id);
        }
      }
    }

    setTrainingForm((prev) => ({
      ...prev,
      excludedGroupIds: ids,
      assignees: sortByLastName(updatedAssignees),
    }));
  };

  /**
   * Validates the training form, collecting all field errors in one pass.
   * Returns true when the form is valid.
   */
  const validateForm = (): boolean => {
    const errors: FieldErrors<CreateDraftField> = {};

    if (trainingForm.title.length < 1) {
      errors.title = 'Title is required';
    }

    if (trainingForm.time === null) {
      errors.time = 'Date is required';
    } else if ((trainingForm.time as any).toString() === 'Invalid Date') {
      errors.time = 'Date is invalid';
    }

    if (trainingForm.description.length < 1) {
      errors.description = 'Description is required';
    }

    if (trainingForm.sport.length < 1) {
      errors.sport = 'Sport is required';
    }

    if (trainingForm.sport_type.length < 1) {
      errors.sport_type = 'Sport type is required';
    }

    if (trainingForm.focus.length < 1) {
      errors.focus = 'Focus is required';
    }

    if (
      !FLOAT_VALIDATION_REGEX.test(trainingForm.distance as any) &&
      !INTEGER_VALIDATION_REGEX.test(trainingForm.distance as any)
    ) {
      errors.distance = 'Distance planned contains invalid characters';
    }

    setFieldErrors(errors);
    scrollToFirstError('create-draft-field', errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Submits the training and the members to assign it to
   *
   */
  const handleSubmit = () => {
    setSubmitError('');
    resetFieldErrors();
    if (validateForm()) {
      setLoading(true);
      const payload = convertAssignTrainingFormToPayload(trainingForm);
      trainingsService
        .createTrainingDraftByTeamId(props.team.id as string, payload)
        .then(() => {
          showSnackbar('Successfully created draft', 'success');
          props.handleRefresh();
          props.handleClose();
        })
        .catch(() => {
          setLoading(false);
          setSubmitError('Error while submitting. Try again');
        });
    }
  };

  /**
   * Submits the training draft and saves it as a template to the training library
   *
   */
  const handleSubmitAndSaveToLibrary = () => {
    setSubmitError('');
    resetFieldErrors();
    if (validateForm()) {
      setLoading(true);
      const payload = convertAssignTrainingFormToPayload(trainingForm);

      // First: Create the training draft
      trainingsService
        .createTrainingDraftByTeamId(props.team.id as string, payload)
        .then((response) => {
          // Extract training_id from the response
          const trainingId = response.data.data.id;

          // Build template payload with required fields
          const templatePayload = {
            training_id: trainingId,
            title: trainingForm.title,
            description: trainingForm.description,
          };

          // Then: Create the template
          return trainingLibraryService.createTrainingTemplate(props.team.id as string, templatePayload);
        })
        .then(() => {
          showSnackbar('Successfully created draft and saved to library', 'success');
          props.handleRefresh();
          props.handleClose();
        })
        .catch((error) => {
          setLoading(false);
          // Check if error is from template creation (draft was already created)
          const isTemplateError = error?.response?.config?.url?.includes('training-library');

          if (isTemplateError) {
            // Template creation failed - close modal, refresh, and show specific message
            showSnackbar('Error when creating training template', 'error');
            props.handleRefresh();
            props.handleClose();
          } else {
            // Draft creation failed
            setSubmitError('Error while submitting. Try again');
          }
        });
    }
  };

  /**
   * Converts the form into the proper assign training payload
   *
   */
  const convertAssignTrainingFormToPayload = (formData: any) => {
    const team = trainingForm.team as any;
    return {
      assignees: formData.assignees.map((assignee: any) => assignee.id),
      teamId: team.id,
      training: {
        title: formData.title,
        time: dayjs(formData.time).format(),
        location: formData.location,
        description: formData.description,
        sport: formData.sport,
        sportType: formData.sport_type,
        focus: formData.focus,
        duration: formData.duration,
        distance: formData.distance,
        actualTimeEnabled: formData.actualTimeEnabled,
        attendanceEnabled: formData.attendanceEnabled,
        postTrainingFormId: formData.postTrainingFormId,
        ...(formData.group && formData.group !== 'team' ? { group: formData.group } : {}),
        excludedGroupIds: formData.excludedGroupIds || [],
      },
    };
  };

  // Field type labels and colors for display
  /**
   * Renders the forms tab content
   */
  const renderFormsTab = () => {
    return (
      <Box className="training-draft-forms-pane">
        <FormControl fullWidth size="small" sx={{ mb: 2, textAlign: 'left' }}>
          <Select
            displayEmpty
            value={trainingForm.postTrainingFormId || ''}
            onChange={(e) => handleFormSelect(e.target.value ? Number(e.target.value) : null)}
            disabled={formsLoading}
            sx={{ borderRadius: '12px', background: '#fff' }}
          >
            <MenuItem value="">
              <em>Select Post-Training Form (Optional)</em>
            </MenuItem>
            {availableForms.map((form) => (
              <MenuItem key={form.id} value={form.id}>
                {form.name} ({form.fields.length} fields)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {formsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {!trainingForm.postTrainingFormId && !formsLoading && (
          <Box className="training-draft-forms-empty">
            <DescriptionOutlinedIcon sx={{ fontSize: 40, color: palette.grey[300], mb: 1.25 }} />
            <Typography className="training-draft-forms-empty__title">No Form Attached</Typography>
            <Typography className="training-draft-forms-empty__desc">
              Select a form above to collect data after this training session
            </Typography>
          </Box>
        )}

        {trainingForm.postTrainingFormId && formDetailsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {trainingForm.postTrainingFormId && selectedForm && !formDetailsLoading && (
          <Paper variant="outlined" sx={{ mt: 1, p: 2, textAlign: 'left', borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                {selectedForm.name}
              </Typography>
              <Typography style={{ fontSize: 13, color: palette.text.secondary }}>
                {selectedForm.fields.length} fields
              </Typography>
            </div>
            {selectedForm.description && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                {selectedForm.description}
              </Typography>
            )}
            <Box>
              {selectedForm.fields.map((field) => {
                const config = FIELD_TYPE_CONFIG[field.field_type];
                return (
                  <Box
                    key={field.field_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      mb: 1,
                      bgcolor: palette.background.paper,
                      borderRadius: 2,
                      border: `1px solid ${palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        flexShrink: 0,
                        mr: 1.5,
                        bgcolor: config?.badgeBg ?? palette.grey[100],
                        color: config?.badgeColor ?? palette.text.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {config?.icon && React.cloneElement(config.icon as React.ReactElement, { sx: { fontSize: 17 } })}
                    </Box>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {field.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.text.secondary, fontSize: 12 }}>
                      {config?.label ?? field.field_type}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  return (
    <COETrainingDraftModal
      open={props.open}
      onClose={props.handleClose}
      title="Create Training"
      subtitle="Build a new training session for your plan"
      badge="DRAFT"
      badgeTone="draft"
      gradientHeader
      headerIcon={<FitnessCenterOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />}
      error={
        submitError || (Object.keys(fieldErrors).length > 0 ? 'Please fix the highlighted fields below' : undefined)
      }
      loading={loading}
      actions={[
        {
          title: 'Create Training',
          color: 'primary',
          variant: 'contained',
          onClick: () => handleSubmit(),
        },
        ...(props.type !== 'template-duplicate'
          ? [
              {
                title: 'Create & Save to Library',
                color: 'primary' as const,
                variant: 'outlined' as const,
                onClick: () => handleSubmitAndSaveToLibrary(),
              },
            ]
          : []),
      ]}
    >
      <div className="training-draft-form-container">
        <div className="training-draft-form-fields">
          <TextField
            id="create-draft-field-title"
            placeholder="Title *"
            name="title"
            required
            value={trainingForm.title}
            onChange={(event) => {
              setTrainingForm((values) => ({ ...values, title: event.target.value }));
              clearFieldError('title');
            }}
            variant="outlined"
            margin="none"
            fullWidth
            error={!!fieldErrors.title}
            helperText={fieldErrors.title || ''}
            sx={{
              '& .MuiInputBase-input': { fontWeight: 600, fontSize: '14px', padding: '14px' },
            }}
          />
          <TextField
            id="create-draft-field-description"
            placeholder="Description *"
            multiline
            required
            margin="none"
            minRows={3}
            maxRows={10}
            value={trainingForm.description}
            error={!!fieldErrors.description}
            helperText={fieldErrors.description || ''}
            onChange={(event) => {
              setTrainingForm((values) => ({ ...values, description: event.target.value }));
              clearFieldError('description');
            }}
            onPaste={(event) => {
              event.preventDefault();
              const plain = event.clipboardData.getData('text/plain');
              const normalized = plain
                .replace(/\r\n/g, '\n')
                .replace(/\r/g, '\n')
                .replace(/<[^>]*>/g, '');
              const target = event.target as HTMLTextAreaElement;
              const start = target.selectionStart ?? trainingForm.description.length;
              const end = target.selectionEnd ?? trainingForm.description.length;
              const next = trainingForm.description.slice(0, start) + normalized + trainingForm.description.slice(end);
              setTrainingForm((values) => ({ ...values, description: next }));
            }}
            sx={{ '& .MuiInputBase-input': { fontSize: '14px', padding: '12px' } }}
          />
          <div className="training-draft-row">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TextField
                id="create-draft-field-time"
                type="time"
                value={dateToTimeString(trainingForm.time)}
                onChange={(event) => {
                  const timeDate = timeStringToDate(event.target.value, trainingForm.time);
                  const combined = combineDateAndTime(trainingForm.time, timeDate);
                  setTrainingForm((values) => ({ ...values, time: combined }));
                  clearFieldError('time');
                }}
                margin="none"
                fullWidth
                error={!!fieldErrors.time}
                helperText={fieldErrors.time || ''}
                inputProps={{ step: 300 }}
                InputProps={{
                  endAdornment: <AccessTimeIcon sx={{ fontSize: 16, color: palette.grey[400] }} />,
                }}
                sx={{ '& .MuiInputBase-input': { fontWeight: 600, fontSize: '14px' } }}
              />
              <DatePicker
                value={trainingForm.time}
                onChange={(date: any) => {
                  const combined = combineDateAndTime(date, trainingForm.time);
                  setTrainingForm((values) => ({
                    ...values,
                    time: combined,
                  }));
                }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    margin="none"
                    fullWidth
                    placeholder="Date"
                    sx={{ '& .MuiInputBase-input': { fontWeight: 600, fontSize: '14px' } }}
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              placeholder="Location"
              name="location"
              value={trainingForm.location}
              onChange={(event) =>
                setTrainingForm((values) => ({
                  ...values,
                  location: event.target.value,
                }))
              }
              variant="outlined"
              margin="none"
              fullWidth
              sx={{ '& .MuiInputBase-input': { fontWeight: 600, fontSize: '14px' } }}
            />
          </div>
          <div className="training-draft-attendance-strip">
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={trainingForm.attendanceEnabled}
                  onChange={(e) => setTrainingForm((values) => ({ ...values, attendanceEnabled: e.target.checked }))}
                />
              }
              label="Track attendance"
              labelPlacement="end"
            />
          </div>
        </div>

        <div className="training-draft-steps-shell">
          <TrainingDraftStepper
            steps={[{ label: 'Training Details' }, { label: 'Members & Groups' }, { label: 'Forms' }]}
            activeStep={activeTab}
            completedSteps={visitedTabs}
            onStepChange={(index) => {
              setVisitedTabs((prev) => new Set(prev).add(activeTab));
              setActiveTab(index);
            }}
          />

          {activeTab === 0 && (
            <div className="training-draft-details-pane">
              <div className="training-draft-section" id="create-draft-field-sport">
                <span
                  className="training-draft-section-title"
                  style={{ color: fieldErrors.sport ? palette.error.main : undefined }}
                >
                  Sport{fieldErrors.sport ? ` — ${fieldErrors.sport}` : ''}
                </span>
                <COEActionBarComponent
                  items={getSportsOptionsFromMapping(sportsMappings)}
                  selectedItem={trainingForm.sport}
                  setSelectedItem={(item: any) => {
                    setTrainingForm((values) => ({ ...values, sport: item }));
                    clearFieldError('sport');
                  }}
                  padding={'3px 10px'}
                  fontSize={14}
                  marginTop={0}
                />
              </div>
              <div
                className="training-draft-section"
                id="create-draft-field-sport_type"
                style={{ display: trainingForm.sport ? 'block' : 'none' }}
              >
                <span
                  className="training-draft-section-title"
                  style={{ color: fieldErrors.sport_type ? palette.error.main : undefined }}
                >
                  Sport Type{fieldErrors.sport_type ? ` — ${fieldErrors.sport_type}` : ''}
                </span>
                <COEActionBarComponent
                  items={getSportsTypeOptionsFromMapping(sportsMappings, trainingForm.sport)}
                  selectedItem={trainingForm.sport_type}
                  setSelectedItem={(item: any) => {
                    setTrainingForm((values) => ({ ...values, sport_type: item }));
                    clearFieldError('sport_type');
                  }}
                  padding={'3px 10px'}
                  fontSize={14}
                  marginTop={0}
                />
              </div>
              <div className="training-draft-section" id="create-draft-field-focus">
                <span
                  className="training-draft-section-title"
                  style={{ color: fieldErrors.focus ? palette.error.main : undefined }}
                >
                  Focus{fieldErrors.focus ? ` — ${fieldErrors.focus}` : ''}
                </span>
                <COEActionBarComponent
                  items={getFocusOptionsFromMapping(sportsMappings, trainingForm.sport)}
                  selectedItem={trainingForm.focus}
                  setSelectedItem={(item: any) => {
                    setTrainingForm((values) => ({ ...values, focus: item }));
                    clearFieldError('focus');
                  }}
                  padding={'3px 10px'}
                  fontSize={14}
                  marginTop={0}
                />
              </div>
              <div className="training-draft-total-row" id="create-draft-field-distance">
                <span className="training-draft-total-row__label">Total</span>
                <div className="training-draft-total-row__box">
                  <AccessTimeIcon sx={{ fontSize: 14, color: palette.grey[400] }} />
                  <COETrainingDurationInput
                    value={trainingForm.duration as any}
                    onChange={(seconds) => {
                      setTrainingForm((values: any) => ({ ...values, duration: seconds }));
                    }}
                  />
                </div>
                <div className="training-draft-total-row__box">
                  <PlaceOutlinedIcon sx={{ fontSize: 14, color: palette.grey[400] }} />
                  <TextField
                    size="small"
                    type="number"
                    value={trainingForm.distance}
                    onChange={(evt) => {
                      setTrainingForm((values: any) => ({ ...values, distance: evt.target.value }));
                      clearFieldError('distance');
                    }}
                    error={!!fieldErrors.distance}
                    helperText={fieldErrors.distance || ''}
                    InputProps={{
                      endAdornment: (
                        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                          {getMeasurementUnit()}
                        </Typography>
                      ),
                    }}
                    sx={{ width: 80 }}
                  />
                </div>
              </div>
            </div>
          )}
          {activeTab === 1 && (
            <COEMemberPanel
              members={assigneeOptions}
              selectedMembers={trainingForm.assignees}
              onMembersChange={handleMembersChange}
              groups={groupOptions}
              selectedGroup={selectedGroup}
              onGroupsChange={handleGroupsChange}
              membersLoading={assigneeOptionsLoading}
              groupsLoading={groupsLoading}
              excludedGroupIds={trainingForm.excludedGroupIds ?? []}
              onExcludedGroupsChange={handleExcludedGroupsChange}
            />
          )}
          {activeTab === 2 && renderFormsTab()}
        </div>
      </div>
    </COETrainingDraftModal>
  );
};

export default CreateTrainingDraftComponent;
